import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from 'src/app/material-module/confirm-dialog/confirm-dialog.component';
import { DisposizioneCompagnia } from 'src/app/models/disposizione-compagnia';
import { dataTableColumnDefinition } from 'src/app/shared/resourceful-index/dataTableColumnDefinition';
import { ResourcefulIndexComponent } from 'src/app/shared/resourceful-index/resourceful-index/resourceful-index.component';
import { DisposizioneCompagniaService } from 'src/app/services/disposizione-compagnia.service';
import { EditDisposizioneCompagniaComponent } from './dialogs/edit/edit.component';
import { DisposizioneAllegatiDialogComponent } from './dialogs/allegati/disposizione-allegati.component';

@Component({
  selector: 'disposizioni-compagnie',
  templateUrl: './disposizioni-compagnie.component.html',
  styleUrls: ['./disposizioni-compagnie.component.scss']
})
export class DisposizioniCompagnieComponent implements OnInit {

  headingText = 'Disposizioni Compagnie';

  relations: string[] = ['allegati', 'gruppo'];

  @ViewChild('index') index: ResourcefulIndexComponent;

  selectionMode = false;

  loading = true;

  search: any;

  columnDefinitions: dataTableColumnDefinition[] = [
    new dataTableColumnDefinition({
      humanName: 'Gruppo',
      internalName: 'gruppo',
      displayFunction: (model: DisposizioneCompagnia) => model['gruppo']?.nome || '',
      sortDisabled: true,
    }),
    new dataTableColumnDefinition({
      humanName: 'Titolo',
      internalName: 'titolo',
    }),
    new dataTableColumnDefinition({
      humanName: 'Contenuto',
      internalName: 'contenuto',
      displayFunction: (model) => model['contenuto']?.substring(0, 50),
    }),
    new dataTableColumnDefinition({
      humanName: 'Data creazione',
      internalName: 'created_at',
      displayFunction: (model: DisposizioneCompagnia) => model.created_at ? model.created_at.format('DD/MM/YYYY HH:mm') : '',
    }),
    new dataTableColumnDefinition({
      humanName: 'Allegati',
      internalName: 'attach_file',
      type: 'button',
      sortDisabled: true,
      onClick: (model: DisposizioneCompagnia) => this.manageAllegati(model),
      displayFunction: () => '',
    }),
  ];

  form: FormGroup;

  constructor(
    public disposizioneService: DisposizioneCompagniaService,
    public dialog: MatDialog,
    private toast: ToastrService,
    @Optional() private dialogRef: MatDialogRef<DisposizioniCompagnieComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
  ) {
    if (this.data?.selectionMode) {
      this.selectionMode = true;
      this.headingText = 'Seleziona una disposizione';
    }
  }

  ngOnInit() {
    this.form = this.fb.group({
      gruppo: [],
      titolo: [],
    });

    this.form.valueChanges.subscribe(newValues => this.search = newValues);
  }

  async ngAfterViewInit(): Promise<void> {
    await this.updateDataSource();
    this.loading = false;
  }

  closeDialogWithSelectedModel(model: DisposizioneCompagnia) {
    this.dialogRef?.close(model);
  }

  async updateDataSource() {
    await this.index?.updateData();
  }

  deleteFunction = async (disposizione: DisposizioneCompagnia) => {
    try {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { message: `Sei sicuro di voler eliminare la disposizione ${disposizione.humanIdentifier}?` }
      });
      dialogRef.afterClosed().subscribe(async data => {
        if (data) {
          await this.disposizioneService.delete(disposizione);
          this.toast.success('Disposizione eliminata.');
          this.updateDataSource();
        }
      });
    } catch (e) {
      console.error(e);
      this.toast.error('Eliminazione disposizione fallita.', 'Errore!');
    }
  }

  editFunction = async (disposizione: DisposizioneCompagnia) => {
    try {
      const dialogRef = this.dialog.open(EditDisposizioneCompagniaComponent, { data: disposizione });
      dialogRef.afterClosed().subscribe(async (data: DisposizioneCompagnia) => {
        if (data) {
          Object.assign(disposizione, data);
          await this.disposizioneService.edit(disposizione);
          this.toast.success('Disposizione modificata');
          this.updateDataSource();
        }
      });
    } catch (e) {
      console.error(e);
      this.toast.error('Modifica disposizione fallita.', 'Errore!');
    }
  }

  add() {
    const addDialogRef = this.dialog.open(EditDisposizioneCompagniaComponent, { data: this.search });
    addDialogRef.afterClosed().subscribe(data => this.handleCreation(data));
  }

  async handleCreation(data?: any) {
    if (data) {
      try {
        const files: File[] = data._files || [];
        delete data._files;
        const created = await this.disposizioneService.create(data);
        const createdId = created?.['id'] || created?.['data']?.['id'];
        if (files.length && createdId) {
          for (const file of files) {
            await this.disposizioneService.uploadAllegato(createdId, file).toPromise();
          }
        }
        this.toast.success('Disposizione creata!');
      } catch (e) {
        this.toast.error('Creazione disposizione fallita.', 'Errore!');
      } finally {
        this.updateDataSource();
      }
    }
  }

  manageAllegati(disposizione: DisposizioneCompagnia) {
    if (!disposizione?.id) {
      return;
    }

    this.dialog.open(DisposizioneAllegatiDialogComponent, {
      width: '600px',
      data: { disposizione }
    });
  }
}
